import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import translations from "../i18n/translations";

const DEFAULT_LANGUAGE = "de";

const LanguageContext = createContext({
  language: DEFAULT_LANGUAGE,
  setLanguage: () => undefined,
  toggleLanguage: () => undefined,
  t: (key, fallback, params) => (params ? fallback ?? key : fallback ?? key),
});

const getStoredLanguage = () => {
  if (typeof window === "undefined") {
    return DEFAULT_LANGUAGE;
  }
  const stored = window.localStorage.getItem("appLanguage");
  if (stored && (stored === "en" || stored === "de")) {
    return stored;
  }
  const browserLang = window.navigator.language?.slice(0, 2).toLowerCase();
  if (browserLang === "en") return "en";
  return DEFAULT_LANGUAGE;
};

const resolveTranslation = (lang, key) => {
  if (!key) return undefined;
  const source = translations[lang];
  if (!source) return undefined;
  return key.split(".").reduce((acc, part) => {
    if (acc && Object.prototype.hasOwnProperty.call(acc, part)) {
      return acc[part];
    }
    return undefined;
  }, source);
};

const formatValue = (value, params) => {
  if (!params || typeof value !== "string") {
    return value;
  }
  return Object.entries(params).reduce(
    (acc, [paramKey, paramValue]) =>
      acc.replace(new RegExp(`{${paramKey}}`, "g"), String(paramValue)),
    value
  );
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(getStoredLanguage);

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.setAttribute("lang", language);
    }
    if (typeof window !== "undefined") {
      window.localStorage.setItem("appLanguage", language);
      window.dispatchEvent(
        new CustomEvent("app-language-change", { detail: { language } })
      );
    }
  }, [language]);

  const toggleLanguage = useCallback(
    () => setLanguage((prev) => (prev === "en" ? "de" : "en")),
    []
  );

  const t = useCallback(
    (key, fallback, params) => {
      const primary = resolveTranslation(language, key);
      if (primary !== undefined) {
        return formatValue(primary, params);
      }

      if (fallback !== undefined) {
        return formatValue(fallback, params);
      }

      if (language !== DEFAULT_LANGUAGE) {
        const defaultValue = resolveTranslation(DEFAULT_LANGUAGE, key);
        if (defaultValue !== undefined) {
          return formatValue(defaultValue, params);
        }
      }

      return key;
    },
    [language]
  );

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      toggleLanguage,
      t,
    }),
    [language, t, toggleLanguage]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);
